<template>
  <div class="ow-book-your-appointment-page">
    <MapSidebar
      :screening-centers="sidebarScreeningCenters"
      :is-disabled="isMapSidebarDisabled"
      :is-live-location-disabled="isLiveLocationDisabled"
      @screening-center-clicked="focusScreeningCenter"
      @lookup-method-updated="screeningCenterLookupMethodUpdated"
      />
    <div class="map-container ow-map-container" :class="{ blur: isOtpContactPopupActive || isOtpInputPopupActive }">
      <OMap ref="bookingMap"
        :map-center="mapCenter"
        :map-options="mapOptions"
        should-include-location-marker
        has-scale
        @popup-open="popupOpen"
        @popup-close="popupClose"
        @location="locationFound"
        @location-error="locationError"
        />
    </div>
    <button v-if="completedPatientBookings && completedPatientBookings.length > 0"
      id="completed-bookings-button"
      class="ow-map-button"
      @click="openCompletedPatientBookingsStep"
      >
      Continue booking: {{ completedPatientBookings.length }} {{ completedPatientBookings.length === 1 ? 'booking' : 'bookings' }}
    </button>
    <PatientOtpComponent v-model="isOtpContactPopupActive" @patient-verified="patientVerified(true)" />
    <PatientBookingComponent ref="patientBookingComponentRef"
      v-model="isPatientBookingPopupActive"
      v-model:completed-bookings="completedPatientBookings"
      :screening-centre="selectedScreeningCentre"
      @booking-finished="finishBooking"
      @popup-closed="bookingPopupClosed"
      />
  </div>
</template>

<script setup lang="ts">
// import { Icon, Marker } from 'leaflet';
import { onMounted, ref } from 'vue';

import { useNuxtApp } from '#app';
import type { IPatientBooking } from '~/components/PatientBookingComponent.vue';

const $router = useRouter();
const { $useToast } = useNuxtApp();

// TEMP ADD THIS WHEN WE START USING SPECIALIST SCS
// const screeningCenterFilters = ref<string[]>([]);
const patientBookingComponentRef = useTemplateRef('patientBookingComponentRef');

// PATIENT VERIFICATION
const isOtpContactPopupActive = ref(false);
const isOtpInputPopupActive = ref(false);
const isPatientVerified = ref<boolean>(false); // If we want to avoid showing dialog while debugging we should set this to true

let patientVerified: ((value: boolean | PromiseLike<boolean>) => void);
// let patientRejected: (reason?: string) => void;
const activateOtpVerification = () => {
  isOtpContactPopupActive.value = true;
  return new Promise<boolean>((resolve) => {
    patientVerified = resolve;
    // patientRejected = reject;
  });
};

// MAP
let mapInstance;
const bookingMap = useTemplateRef('bookingMap');
const userCurrentLocation = ref<geoLocation>();
const mapCenter = ref<geoLocation>([-27.008135135942506, 23.832263980924836]);
const mapOptions = ref({
  zoom: 6,
  minZoom: 3,
  maxZoom: 18,
  setView: true,
  enableHighAccuracy: true,
  maxBounds: [[-90, -180], [90, 180]],
  maxBoundsViscosity: 1.0,
  center: [-27.008135135942506, 23.832263980924836]
});
const geoJsonData = ref<IGeoJson>();

const selectedScreeningCentre = ref<ISubOrganization>();

const sidebarScreeningCenters = ref<ISubOrganization[]>([]);

// PATIENT BOOKING
interface IPatientBookingRequestBody {
  patient?: IPatient
  patientId?: string
  calEvent: {
    start: string
    end: string
    fk_hospital: string
  }
}

const isPatientBookingPopupActive = ref(false);
const completedPatientBookings = ref<IPatientBooking[]>([]);

const saveBookingToDatabase = async (patientBooking: IPatientBooking) => {
  try {
    if (!patientBooking || !patientBooking.screeningCentre) throw new Error('There was an error booking');
    const newBooking: IPatientBookingRequestBody = {
      calEvent: {
        start: patientBooking.timeslot.start,
        end: patientBooking.timeslot.end,
        fk_hospital: patientBooking.screeningCentre._id
      }
    };
    if (patientBooking.patientId) {
      newBooking.patientId = patientBooking.patientId;
    }
    else {
      newBooking.patient = { ...patientBooking.patient, fk_organization: patientBooking.screeningCentre.organizationId, enttName: 'patient' };
    }
    const response = await $fetch('/api/endpoints/post-calendar-event', {
      method: 'POST',
      body: newBooking
    });
  }
  catch (error) {
    console.log(error);
  }
};

const activatePatientBooking = () => {
  isPatientBookingPopupActive.value = true;
};

const openCompletedPatientBookingsStep = () => {
  patientBookingComponentRef.value?.switchToStep(4);
};

const finishBooking = async () => {
  completedPatientBookings.value?.forEach((patientBooking: IPatientBooking) => {
    saveBookingToDatabase(patientBooking);
  });
  completedPatientBookings.value = [];
};

const bookingPopupClosed = async () => {
  setTimeout(() => {
    $router.push('/');
  }, 300);
};

// SCREENING CENTERS
const selectScreeningCentre = (subOrganization?: ISubOrganization) => {
  if (subOrganization) selectedScreeningCentre.value = subOrganization;
  activatePatientBooking();
};

/** This function generates the request body based on the method and the value */
const generateRequestBody = (method: string, searchParameter: string) => {
  let requestBody = {};

  if (method === 'live') {
    const patientGeoLocation = userCurrentLocation.value ? { latitude: userCurrentLocation.value[0], longitude: userCurrentLocation.value[1] } : {};
    requestBody = {
      patientGeoLocation,
      range: Number(searchParameter) || 5
    };
  }
  else if (method === 'areaCode') {
    requestBody = {
      zipCode: searchParameter
    };
  }
  else if (method === 'screeningCenter') {
    requestBody = {
      name: searchParameter
    };
  }
  return requestBody;
};

/** This function fetches the screning centers based on the method of fetching and the value selected in the sidebar
 * @param method represents the name of the method used to fetch screening centers, can be 'live', 'areaCode' and 'screeningCenter'
 * @param value represents the value of the inputs related to the method. This can be zip code string or lat/lng
 * When using live it represents the range we are fetching from and in other methots it is the search value for the area code or name of SC
 */
const getScreeningCenters = async (method: string, searchParameter: string) => {
  if (isPatientVerified.value) {
    const requestBody = generateRequestBody(method, searchParameter);

    const screeningCenters = await $fetch('/api/endpoints/get-screening-centers', { method: 'POST', body: requestBody });

    if (screeningCenters && screeningCenters.features.length === 0) {
      let message = '';
      if (method === 'areaCode') {
        message = 'No screening centres found for this ZIP code. Please try a different location.';
      }
      else if (method === 'screeningCenter') {
        message = 'No screening centres found under this name. Please try a different location.';
      }
      else if (method === 'live') {
        message = 'No screening centres found in the desired area. Please try a different range.';
      }
      $useToast().show({ message, className: 'is-alert', duration: 5000 });
    }

    geoJsonData.value = screeningCenters;

    return screeningCenters;
  }
  return { features: [] };
};

const focusBoundingBox = (screeningCenters: IGeoJson) => {
  const geoLocations = screeningCenters.features.map((feature: IGeoJsonFeature) => feature.properties.geoLocation);

  const latitudes = geoLocations.map((geoLocation: geoLocation) => geoLocation[0]).sort();
  const longitudes = geoLocations.map((geoLocation: geoLocation) => geoLocation[1]).sort();

  const southWestCorner = [latitudes[0], longitudes[0]];
  const northEastCorner = [latitudes[latitudes.length - 1], longitudes[longitudes.length - 1]];
  // , { maxZoom: 20, padding: 50 }
  mapInstance?.fitBounds([southWestCorner, northEastCorner], { maxZoom: 16 });
};

const screeningCenterLookupMethodUpdated = async (method: string, searchParameter: string) => {
  const screeningCenters = await getScreeningCenters(method, searchParameter);

  if (method !== 'screeningCenter') {
    bookingMap.value?.updateGeoJSON(screeningCenters, true);
    sidebarScreeningCenters.value = [] as ISubOrganization[];
    // If patients location is present zoom and pan to that location
    if (method === 'live' && userCurrentLocation.value) {
      const userLatLngLocation = userCurrentLocation.value;
      bookingMap.value?.setCenter(userLatLngLocation, getLiveLocationZoom(searchParameter));
    }
    // After fetchin and if we have fetched using area code we zoom and pan to the area
    if (method === 'areaCode') {
      if (screeningCenters && screeningCenters.features && screeningCenters.features.length > 0 && screeningCenters.features[0]) {
        focusBoundingBox(screeningCenters);
        // focusScreeningCenter(screeningCenters.features[0].properties.geoLocation);
      }
    }
  }
  else {
    if (screeningCenters && screeningCenters.features && screeningCenters.features.length > 0) {
      bookingMap.value?.updateGeoJSON({ features: [], type: 'FeatureCollection' }, true);
      sidebarScreeningCenters.value = screeningCenters.features.map((feature: IGeoJsonFeature) => {
        return {
          ...feature.properties.subOrganization,
          geoLocation: feature.properties.geoLocation
        };
      });
    }
  };
};

// POPUPS
interface IPopupData {
  iconUrl: string
  subOrganization: ISubOrganization
}

const popupOpen = (popupHtml: HTMLElement, popupData: IPopupData) => {
  const button = popupHtml.querySelector('#book-appointment-button');
  if (button) {
    button.addEventListener('click', () => selectScreeningCentre(popupData.subOrganization));
  }
};

const popupClose = (popupHtml: HTMLElement) => {
  if (popupHtml) {
    const button = popupHtml.querySelector('#book-appointment-button');
    if (button) {
      button.removeEventListener('click', () => selectScreeningCentre);
    }
  }
  selectedScreeningCentre.value = {} as ISubOrganization;
};

// LOCATION
const isMapSidebarDisabled = ref(true);
const isLiveLocationDisabled = ref(false);

/** Returns the amount the map should be zoomed based on the range */
const getLiveLocationZoom = (range?: string) => {
  // this if statement decides how much to zoom
  let zoom = 14;
  if (range) {
    switch (Number(range)) {
      case 10:
        zoom = 13;
        break;
      case 15:
        zoom = 12;
        break;
      case 20:
        zoom = 11;
        break;
      case 25:
        zoom = 10;
        break;
      case 30:
        zoom = 10;
        break;
    }
  };
  return zoom;
};

const focusScreeningCenter = (geoLocation: geoLocation, screeningCenter?: ISubOrganization) => {
  bookingMap.value?.updateGeoJSON(geoJsonData.value?.features.filter(feature => feature.properties.subOrganization._id === screeningCenter?._id), true);
  bookingMap.value?.setCenter(geoLocation, 15);
};

const locationError = async () => {
  isLiveLocationDisabled.value = true;
};

const locationFound = async (deviceLocationEvent: { longitude: number, latitude: number }) => {
  userCurrentLocation.value = [deviceLocationEvent.latitude, deviceLocationEvent.longitude];
  // Find screening centers in rage by default
  const availableScreeningCenters = await getScreeningCenters('live', '5');
  // Show found screening centers on map
  bookingMap.value?.updateGeoJSON(availableScreeningCenters, true);

  bookingMap.value?.setZoom(getLiveLocationZoom());
};

/** This will initialize booking map with screening center pins
 * 1. First force user to generate and type OTP
 * 2. Initialize new map instance
 * 3. By default search Screening centers by user location
 * 4. Show screening centers on map
 */
const initBookingMap = async () => {
  // After verification we need to save isPatientVerified flag for future use
  isPatientVerified.value = await activateOtpVerification();
  mapInstance = bookingMap.value?.initMap(null, true);
  isMapSidebarDisabled.value = false;
};

onMounted(() => {
  setTimeout(() => {
    initBookingMap();
  }, 100);
});
</script>

<style lang="scss">
.ow-book-your-appointment-page {
  height: 100%;
  display: flex;
  & .ow-map-container  {
    flex: 1;
    z-index: 0;
  }
}

.gps_ring {
  background-color: unset;
  width: 15px;
  height: 15px;
  animation: unset;
  position: relative;
  border: none;
  background: radial-gradient(circle at center, #ff1938 0%, #ff1938 50%, #fff 51%, #fff 60%, transparent 61%) center center no-repeat;
  animation: pulse 0.75s linear infinite alternate;
  &:hover {
    &::before,
    &::after {
      visibility: visible;
    }
  }
  &::before {
    content: 'Your location';
    text-align: center;
    visibility: hidden;
    background-color: #757575e0;
    color: #fff;
    text-shadow: 0 0 2px #000;
    padding: 5px 10px;
    border-radius: 10px;
    position: absolute;
    bottom: calc(100% + 15px);
    left: 50%;
    transform: translateX(-50%);
  }
  &::after {
    content: '';
    visibility: hidden;
    box-sizing: border-box;
    bottom: 10px;
    width: 15px;
    height: 15px;
    left: 50%;
    transform: translateX(-50%);
    position: absolute;
    border-width: 10px;
    border-style: solid;
    border-color: #757575e0 transparent transparent transparent;
  }
}
#completed-bookings-button {
  z-index: 100;
  position: fixed;
  display: flex;
  bottom: 50px;
  width: auto;
  align-items: center;
  padding: 20px;
  top: unset;
  left: 50%;
  transition: color 0.2s ease-out, backround-color 0.2s ease-out;
  font-weight: bold;
  color: #0e365d;
  cursor: pointer;
  &:hover {
    background-color: #0e365d;
    color: #fff;
    transition: color 0.2s ease-in, backround-color 0.2s ease-in;
    & .os-icon {
      background-color: #fff;
    }
  }
}
@keyframes pulse{
  from {
    background-size: 80% 80%;
  }
  to {
    background-size: 120% 120%;
  }
}

.leaflet-marker-icon.leaflet-zoom-animated.leaflet-interactive {
  @media only screen and (max-width:620px) {
    width: 35px !important;
    height: 35px !important;
  }
}
</style>
